import React, { useState, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  MessageSquare,
  Search,
  CheckSquare,
  Clock,
  TrendingUp,
  ClipboardList,
  ArrowRightLeft,
  BarChart3,
  CalendarRange,
  CalendarCheck,
  Activity,
  Archive,
  Boxes,
  Wrench,
  Shield,
  Zap,
  Folder,
  Sparkles,
  BookOpen,
  FileCode,
  Package,
  Layers,
  FileText,
  Calculator,
  Users,
  Workflow,
  RotateCcw,
} from "lucide-react";
import {
  loadSavedTabOrder,
  saveTabOrder,
  removeSavedTabOrder,
  resolveBlockOrder,
  moveBlock,
  groupBlockId,
  isGroupBlock,
  groupNameFromBlock,
  addTabToGroup,
  removeTabFromGroup,
  type DropPosition,
} from "./navStorage";
import { updateUserPreferences } from "../lib/userAdminData";
import { showToast } from "../utils";
import { NavGroup, NavItem } from "./AppNavParts";
export { NavGroup, NavItem } from "./AppNavParts";

// --- NAVIGATION HELPER COMPONENTS ---

/**
 * Container component that renders a list of navigation menu items and groups.
 * Dynamically filters items based on user permissions (`allowedTabs`), groups items by
 * configured menu groups, and positions administrative tools in a dedicated section.
 *
 * @param {Object} props - Component properties.
 * @param {"v1"|"v2"} props.uiVersion - Current global UI layout version.
 * @param {Object} props.user - The currently authenticated user object containing roles and menu configurations.
 * @param {Array<string>} props.allowedTabs - Array of tab IDs that the user is permitted to view.
 * @param {Object} props.expandedGroups - Key-value map of expanded menu group states.
 * @param {string} props.activeTab - Currently active tab/route ID.
 * @param {Function} props.onNavClick - Navigation handler function triggered when a tab is clicked.
 * @param {Function} props.toggleGroup - Callback function to toggle the expansion of menu groups.
 * @param {boolean} props.hasUnreadSupportTickets - Flag indicating whether the admin section has unread support alerts.
 * @param {number} [props.chatUnreadCount=0] - Unread messenger message count for the Chat tab badge.
 * @param {boolean} [props.isCollapsed=false] - Whether the sidebar is collapsed.
 * @returns {JSX.Element} The rendered list of navigation menu elements.
 */
export function NavItemsList({
  user,
  allowedTabs,
  expandedGroups,
  activeTab,
  onNavClick,
  toggleGroup,
  hasUnreadSupportTickets,
  hasPendingHandover,
  chatUnreadCount = 0,
  tenantConfig,
  isCollapsed = false,
}: {
  user: any;
  allowedTabs: string[];
  expandedGroups: Record<string, boolean | undefined>;
  activeTab: string;
  onNavClick: (tabId: string) => void;
  toggleGroup: (groupName: string) => void;
  hasUnreadSupportTickets: boolean;
  hasPendingHandover: boolean;
  chatUnreadCount?: number;
  tenantConfig: any;
  isCollapsed?: boolean;
}) {
  const { t } = useTranslation();
  const tabs = [
    {
      id: "dashboard",
      icon: <LayoutDashboard size={18} />,
      label: t("tabs.dashboard"),
    },
    {
      id: "reservations",
      icon: <CalendarRange size={18} />,
      label: t("tabs.reservations", "Reservierungen"),
    },
    {
      id: "explorer",
      icon: <Search size={18} />,
      label: t("tabs.explorer", "Explorer"),
    },
    {
      id: "roomrack",
      icon: <CalendarRange size={18} />,
      label: t("tabs.roomrack", "Zimmerplan"),
    },
    {
      id: "availability",
      icon: <CalendarCheck size={18} />,
      label: t("tabs.availability", "Verfügbarkeiten"),
    },
    {
      id: "analytics",
      icon: <BarChart3 size={18} />,
      label: t("tabs.analytics"),
    },
    {
      id: "traces",
      icon: <CheckSquare size={18} />,
      label: t("tabs.traces"),
    },
    {
      id: "hk",
      icon: <ClipboardList size={18} />,
      label: t("tabs.hk"),
    },
    {
      id: "ht",
      icon: <Wrench size={18} />,
      label: t("tabs.ht"),
    },
    {
      id: "internalresources",
      icon: <Boxes size={18} />,
      label: t("tabs.internalresources", "Interne Ressourcen"),
    },
    {
      id: "fundsachen",
      icon: <Archive size={18} />,
      label: t("tabs.fundsachen"),
    },
    {
      id: "hr",
      icon: <Clock size={18} />,
      label: t("tabs.hr"),
    },
    {
      id: "chat",
      icon: <MessageSquare size={18} />,
      label: t("tabs.chat", "Chat (Prototyp)"),
    },
    {
      id: "handover",
      icon: <ArrowRightLeft size={18} />,
      label: t("tabs.handover", "Schichtübergabe"),
    },
    {
      id: "sales",
      icon: <TrendingUp size={18} />,
      label: t("tabs.sales"),
    },
    {
      id: "iframe",
      icon: <Sparkles size={18} />,
      label: tenantConfig?.customIframeName || t("tabs.iframe", "Partner-App"),
    },
    {
      id: "kassenbuch",
      icon: <BookOpen size={18} />,
      label: t("tabs.kassenbuch"),
    },
    {
      id: "xrechnung",
      icon: <FileCode size={18} />,
      label: t("tabs.xrechnung"),
    },
    {
      id: "buchhaltung",
      icon: <Calculator size={18} />,
      label: t("tabs.buchhaltung", "Buchhaltung"),
    },
    {
      id: "anleitungen",
      icon: <Folder size={18} />,
      label: t("tabs.anleitungen"),
    },
    {
      id: "inventory",
      icon: <Package size={18} />,
      label: t("tabs.inventory"),
    },
    {
      id: "groups",
      icon: <Layers size={18} />,
      label: t("tabs.groups", "Gruppen & Blocks"),
    },
    {
      id: "offers",
      icon: <FileText size={18} />,
      label: t("tabs.offers", "Angebote"),
    },
    {
      id: "users",
      icon: <Users size={18} />,
      label: t("tabs.users"),
    },
    {
      id: "logs",
      icon: <Activity size={18} />,
      label: t("tabs.logs"),
    },
    {
      id: "admin",
      icon: <Shield size={18} />,
      label: t("tabs.admin"),
    },
    {
      id: "automations",
      icon: <Zap size={18} />,
      label: t("tabs.automations", "Automatisierungen"),
    },
    {
      id: "workflows",
      icon: <Workflow size={18} />,
      label: t("tabs.workflows", "Workflows"),
    },
  ];

  // Saved order is read once per user id. Keying the state on the id (and
  // resetting during render when it changes) avoids the setState-in-effect
  // cascade the React Compiler rejects — see the React docs' "adjusting state
  // when a prop changes" pattern.
  const [blockOrder, setBlockOrder] = useState<string[]>(() =>
    loadSavedTabOrder(user?.id),
  );
  const [blockOrderUserId, setBlockOrderUserId] = useState(user?.id);
  if (blockOrderUserId !== user?.id) {
    setBlockOrderUserId(user?.id);
    setBlockOrder(loadSavedTabOrder(user?.id));
  }
  // Optimistic overlay for group membership. `null` means "no pending change",
  // so the profile value is used directly and the two can never drift.
  const [pendingGroups, setPendingGroups] = useState<any[] | null>(null);
  const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);
  const [dragOverBlockId, setDragOverBlockId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<DropPosition>("after");
  const [dropOnGroup, setDropOnGroup] = useState<string | null>(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const dragHappenedRef = useRef(false);
  const draggedItemRectRef = useRef<DOMRect | null>(null);

  // Group membership lives in the profile (users.menu_groups). While a drop is
  // being saved we render the optimistic value; otherwise the profile is the
  // single source of truth, so an edit from the preferences modal shows up
  // without any state-syncing effect.
  //
  // Memoized on the SERIALIZED value: `user.menuGroups` is a fresh array on
  // every parent render, so depending on its identity would rebuild every
  // downstream callback and re-render the whole sidebar on each parent update.
  const menuGroupsKey = JSON.stringify(user?.menuGroups || []);
  const menuGroups = useMemo(
    () => pendingGroups ?? (Array.isArray(user?.menuGroups) ? user.menuGroups : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingGroups, menuGroupsKey],
  );
  const groupedTabIds = menuGroups.flatMap((g: any) => g.items);
  const ungroupedTabs = tabs.filter(
    (t) => allowedTabs.includes(t.id) && !groupedTabIds.includes(t.id),
  );

  const adminTabIds = ["users", "logs", "automations"];
  const standardUngrouped = ungroupedTabs.filter(
    (t) => !adminTabIds.includes(t.id),
  );
  const adminUngrouped = ungroupedTabs.filter((t) =>
    adminTabIds.includes(t.id),
  );

  // Default arrangement: menu groups first, then the loose tabs.
  //
  // These derivations are intentionally NOT wrapped in useMemo: the React
  // Compiler already memoizes them, and hand-written memos over these
  // dependencies are what it reports as "could not preserve existing
  // memoization" (the deps include arrays rebuilt on every render).
  const defaultBlockIds = [
    ...menuGroups.map((g: any) => groupBlockId(g.name)),
    ...standardUngrouped.map((t) => t.id),
  ];

  const orderedBlockIds = resolveBlockOrder(defaultBlockIds, blockOrder);

  const orderedStandardTabs = orderedBlockIds
    .filter((id) => !isGroupBlock(id))
    .map((id) => standardUngrouped.find((t) => t.id === id))
    .filter((t): t is (typeof standardUngrouped)[number] => Boolean(t));

  const orderedGroups = orderedBlockIds
    .filter(isGroupBlock)
    .map((id) => menuGroups.find((g: any) => g.name === groupNameFromBlock(id)))
    .filter(Boolean);

  const hasCustomOrder =
    blockOrder.length > 0 &&
    (defaultBlockIds.length !== orderedBlockIds.length ||
      defaultBlockIds.some((id, index) => id !== orderedBlockIds[index]));

  // Persist the new block order locally (presentation-only, per device).
  const commitBlockOrder = useCallback(
    (next: string[]) => {
      setBlockOrder(next);
      saveTabOrder(next, user?.id);
    },
    [user?.id],
  );

  /**
   * Write group membership to the user's profile.
   *
   * Order INSIDE a group is presentation-only and stays in localStorage, but
   * MEMBERSHIP is persisted because the preferences modal edits the same
   * field. The optimistic update is rolled back when the write fails, so the
   * sidebar never shows a group state the database does not have.
   */
  const commitGroups = useCallback(
    async (nextGroups: any[]) => {
      setPendingGroups(nextGroups);
      setSavingGroup(true);
      try {
        const { error } = await updateUserPreferences(
          user?.tenantId,
          user?.id,
          { menu_groups: nextGroups },
        );
        if (error) {
          setPendingGroups(null);
          showToast(
            t(
              "tabs.groupSaveError",
              "Gruppe konnte nicht gespeichert werden. Bitte erneut versuchen.",
            ),
            "error",
          );
        }
      } catch {
        setPendingGroups(null);
        showToast(
          t(
            "tabs.groupSaveError",
            "Gruppe konnte nicht gespeichert werden. Bitte erneut versuchen.",
          ),
          "error",
        );
      } finally {
        setSavingGroup(false);
      }
    },
    [user?.tenantId, user?.id, t],
  );

  const handleReorder = useCallback(
    (sourceId: string, targetId: string, position: DropPosition) => {
      const next = moveBlock(orderedBlockIds, sourceId, targetId, position);
      if (next === orderedBlockIds) return;
      commitBlockOrder(next);
    },
    [orderedBlockIds, commitBlockOrder],
  );

  /** Dropping a loose tab ON a group header adds it to that group. */
  const handleDropOnGroup = useCallback(
    (tabId: string, groupName: string) => {
      const next = addTabToGroup(menuGroups, tabId, groupName);
      if (!next) return;
      void commitGroups(next);
    },
    [menuGroups, commitGroups],
  );

  /** Dropping a grouped tab on a loose tab pulls it back out of its group. */
  const handleDropOnLooseTab = useCallback(
    (tabId: string) => {
      const next = removeTabFromGroup(menuGroups, tabId);
      if (!next) return;
      void commitGroups(next);
    },
    [menuGroups, commitGroups],
  );

  const handleResetOrder = useCallback(() => {
    setBlockOrder([]);
    removeSavedTabOrder(user?.id);
  }, [user?.id]);

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
    setDraggedBlockId(id);
    dragHappenedRef.current = true;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    setDragOverBlockId(targetId);
    setDropPosition(e.clientY < midY ? "before" : "after");
  }, []);

  const handleDragLeave = useCallback(
    (e: React.DragEvent, targetId: string) => {
      if (
        dragOverBlockId === targetId &&
        !e.currentTarget.contains(e.relatedTarget as Node)
      ) {
        setDragOverBlockId(null);
      }
    },
    [dragOverBlockId],
  );
  const handleDrop = useCallback(
    (e: React.DragEvent, targetId: string) => {
      e.preventDefault();
      const sourceId = e.dataTransfer.getData("text/plain") || draggedBlockId;
      if (sourceId && sourceId !== targetId) {
        // A grouped tab dropped on a loose tab leaves its group.
        if (!isGroupBlock(sourceId) && !isGroupBlock(targetId)) {
          handleDropOnLooseTab(sourceId);
        }
        // Resolve the position from the drop event rather than the dragOver
        // state: a drop can arrive before the last dragOver state has
        // committed, which would apply a stale before/after.
        const rect = e.currentTarget.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        handleReorder(
          sourceId,
          targetId,
          e.clientY < midY ? "before" : "after",
        );
      }
      setDraggedBlockId(null);
      setDragOverBlockId(null);
      setDropOnGroup(null);
    },
    [draggedBlockId, handleReorder, handleDropOnLooseTab],
  );

  /**
   * Drag-over handler for a group BODY (the area holding the group's items).
   * Hovering it means "add the dragged tab to this group", so it highlights the
   * body instead of showing a before/after insert line. The group HEADER keeps
   * the normal reorder behaviour, which is what lets a loose tab be dropped
   * above or between groups.
   */
  const handleGroupBodyDragOver = useCallback(
    (e: React.DragEvent, groupName: string) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      // A group block can never be nested inside another group.
      if (draggedBlockId && !isGroupBlock(draggedBlockId)) {
        setDropOnGroup(groupName);
        setDragOverBlockId(null);
      }
    },
    [draggedBlockId],
  );

  const handleGroupBodyDragLeave = useCallback(
    (e: React.DragEvent, groupName: string) => {
      // Ignore bubbling from the items rendered inside the body.
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setDropOnGroup((current) => (current === groupName ? null : current));
      }
    },
    [],
  );

  /** Drop handler for a group BODY: the dragged loose tab JOINS the group. */
  const handleGroupBodyDrop = useCallback(
    (e: React.DragEvent, groupName: string) => {
      e.preventDefault();
      e.stopPropagation();
      const sourceId = e.dataTransfer.getData("text/plain") || draggedBlockId;
      if (sourceId && !isGroupBlock(sourceId)) {
        handleDropOnGroup(sourceId, groupName);
      }
      setDraggedBlockId(null);
      setDragOverBlockId(null);
      setDropOnGroup(null);
    },
    [draggedBlockId, handleDropOnGroup],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedBlockId(null);
    setDragOverBlockId(null);
    setDropOnGroup(null);
    setTimeout(() => {
      dragHappenedRef.current = false;
    }, 120);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent, id: string) => {
    const touch = e.touches[0];
    if (!touch) return;

    dragHappenedRef.current = true;
    setDraggedBlockId(id);
    setDragOverBlockId(null);

    const currentTarget = e.currentTarget as HTMLElement;
    const navItem = currentTarget.closest("[data-block-id]") as HTMLElement | null;
    if (navItem) {
      draggedItemRectRef.current = navItem.getBoundingClientRect();
    }

    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      try {
        navigator.vibrate(25);
      } catch {
        // Ignore haptic feedback errors if not supported
      }
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!draggedBlockId) return;
      const touch = e.touches[0];
      if (!touch) return;

      if (e.cancelable) {
        e.preventDefault();
      }

      let elem = document.elementFromPoint(touch.clientX, touch.clientY);
      let blockEl = elem?.closest("[data-block-id]") as HTMLElement | null;

      if (!blockEl && draggedItemRectRef.current) {
        const centerX =
          draggedItemRectRef.current.left +
          draggedItemRectRef.current.width / 2;
        elem = document.elementFromPoint(centerX, touch.clientY);
        blockEl = elem?.closest("[data-block-id]") as HTMLElement | null;
      }

      if (!blockEl) {
        setDragOverBlockId(null);
        setDropOnGroup(null);
        return;
      }

      const targetId = blockEl.getAttribute("data-block-id");
      if (!targetId || targetId === draggedBlockId) {
        setDragOverBlockId(null);
        setDropOnGroup(null);
        return;
      }

      if (!orderedBlockIds.includes(targetId)) {
        setDragOverBlockId(null);
        setDropOnGroup(null);
        return;
      }

      const rect = blockEl.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      setDragOverBlockId(targetId);
      setDropPosition(touch.clientY < midY ? "before" : "after");
      // Joining a group requires hovering its BODY. Hovering the group header
      // keeps the before/after insert line, so a tab can still be positioned
      // above or between groups on touch.
      const bodyGroup =
        (
          elem?.closest("[data-group-body]") as HTMLElement | null
        )?.getAttribute("data-group-body") || null;
      setDropOnGroup(
        bodyGroup && !isGroupBlock(draggedBlockId) ? bodyGroup : null,
      );

      // Auto-scroll the sidebar container if dragging near the top or bottom edge
      const navContainer = blockEl.closest("nav");
      if (navContainer) {
        const cRect = navContainer.getBoundingClientRect();
        const edgeThreshold = 48;
        if (touch.clientY < cRect.top + edgeThreshold) {
          navContainer.scrollTop -= 6;
        } else if (touch.clientY > cRect.bottom - edgeThreshold) {
          navContainer.scrollTop += 6;
        }
      }
    },
    [draggedBlockId, orderedBlockIds],
  );

  const handleTouchEnd = useCallback(() => {
    if (draggedBlockId && dragOverBlockId && draggedBlockId !== dragOverBlockId) {
      if (dropOnGroup && !isGroupBlock(draggedBlockId)) {
        handleDropOnGroup(draggedBlockId, dropOnGroup);
      } else {
        if (!isGroupBlock(draggedBlockId) && !isGroupBlock(dragOverBlockId)) {
          handleDropOnLooseTab(draggedBlockId);
        }
        handleReorder(draggedBlockId, dragOverBlockId, dropPosition);
      }
    }
    setDraggedBlockId(null);
    setDragOverBlockId(null);
    setDropOnGroup(null);
    draggedItemRectRef.current = null;
    setTimeout(() => {
      dragHappenedRef.current = false;
    }, 200);
  }, [
    draggedBlockId,
    dragOverBlockId,
    dropPosition,
    dropOnGroup,
    handleDropOnGroup,
    handleDropOnLooseTab,
    handleReorder,
  ]);

  const handleTouchCancel = useCallback(() => {
    setDraggedBlockId(null);
    setDragOverBlockId(null);
    setDropOnGroup(null);
    draggedItemRectRef.current = null;
    setTimeout(() => {
      dragHappenedRef.current = false;
    }, 200);
  }, []);

  const handleItemClick = useCallback(
    (tabId: string) => {
      if (dragHappenedRef.current) return;
      onNavClick(tabId);
    },
    [onNavClick],
  );

  return (
    <>
      {orderedGroups.map((group: any) => {
        const visibleItems = group.items.filter((id: string) =>
          allowedTabs.includes(id),
        );
        if (visibleItems.length === 0) return null;

        const gBlockId = groupBlockId(group.name);
        const isGroupDropTarget = dropOnGroup === group.name;

        return (
          <NavGroup
            key={group.name}
            blockId={gBlockId}
            name={group.name}
            color={group.color}
            isCollapsed={isCollapsed}
            expanded={(() => {
              const entry = Object.entries(expandedGroups || {}).find(
                ([k]) => k === group.name,
              );
              return entry ? entry[1] !== false : true;
            })()}
            onToggle={() => toggleGroup(group.name)}
            isDraggable={true}
            isDragging={draggedBlockId === gBlockId}
            isDragOver={dragOverBlockId === gBlockId}
            dropPosition={dropPosition}
            isJoinTarget={isGroupDropTarget}
            onDragStart={(e) => handleDragStart(e, gBlockId)}
            onDragOver={(e) => {
              // Header = reorder target (before/after this group block).
              handleDragOver(e, gBlockId);
              if (dropOnGroup === group.name) setDropOnGroup(null);
            }}
            onDragLeave={(e) => handleDragLeave(e, gBlockId)}
            onDrop={(e) => handleDrop(e, gBlockId)}
            onDragEnd={handleDragEnd}
            onBodyDragOver={(e) => handleGroupBodyDragOver(e, group.name)}
            onBodyDragLeave={(e) => handleGroupBodyDragLeave(e, group.name)}
            onBodyDrop={(e) => handleGroupBodyDrop(e, group.name)}
          >
            {visibleItems.map((id: string) => {
              const tab = tabs.find((t) => t.id === id);
              if (!tab) return null;
              return (
                <NavItem
                  key={tab.id}
                  icon={tab.icon}
                  label={tab.label}
                  active={activeTab === tab.id}
                  onClick={() => handleItemClick(tab.id)}
                  hasRedDot={
                    (tab.id === "admin" && hasUnreadSupportTickets) ||
                    (tab.id === "handover" && hasPendingHandover)
                  }
                  badgeCount={tab.id === "chat" ? chatUnreadCount : 0}
                  isCollapsed={isCollapsed}
                />
              );
            })}
          </NavGroup>
        );
      })}

      {orderedStandardTabs.map((tab) => (
        <NavItem
          key={tab.id}
          tabId={tab.id}
          icon={tab.icon}
          label={tab.label}
          active={activeTab === tab.id}
          onClick={() => handleItemClick(tab.id)}
          hasRedDot={
            (tab.id === "admin" && hasUnreadSupportTickets) ||
            (tab.id === "handover" && hasPendingHandover)
          }
          badgeCount={tab.id === "chat" ? chatUnreadCount : 0}
          isCollapsed={isCollapsed}
          isDraggable={true}
          isDragging={draggedBlockId === tab.id}
          isDragOver={dragOverBlockId === tab.id}
          dropPosition={dragOverBlockId === tab.id ? dropPosition : null}
          onDragStart={(e) => handleDragStart(e, tab.id)}
          onDragOver={(e) => handleDragOver(e, tab.id)}
          onDragLeave={(e) => handleDragLeave(e, tab.id)}
          onDrop={(e) => handleDrop(e, tab.id)}
          onDragEnd={handleDragEnd}
          onTouchStart={(e) => handleTouchStart(e, tab.id)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        />
      ))}

      {(hasCustomOrder || savingGroup) && !isCollapsed && (
        <div className="flex items-center justify-between px-2 pt-1 pb-1">
          <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            {savingGroup
              ? t("tabs.savingGroup", "Gruppe wird gespeichert…")
              : t("tabs.customOrder", "Eigene Anordnung")}
          </span>
          {hasCustomOrder && (
            <button
              type="button"
              onClick={handleResetOrder}
              title={t(
                "tabs.resetOrderTooltip",
                "Standardreihenfolge wiederherstellen",
              )}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer py-0.5 px-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <RotateCcw size={10} />
              <span>{t("tabs.reset", "Zurücksetzen")}</span>
            </button>
          )}
        </div>
      )}

      {adminUngrouped.length > 0 && (
        <div className={`mt-4 border-t pt-4 border-slate-100 dark:border-slate-800 space-y-1 ${isCollapsed ? "flex flex-col items-center" : ""}`}>
          {adminUngrouped.map((tab) => (
            <NavItem
              key={tab.id}
              icon={tab.icon}
              label={tab.label}
              active={activeTab === tab.id}
              onClick={() => handleItemClick(tab.id)}
              hasRedDot={
                (tab.id === "admin" && hasUnreadSupportTickets) ||
                (tab.id === "handover" && hasPendingHandover)
              }
              badgeCount={tab.id === "chat" ? chatUnreadCount : 0}
              isCollapsed={isCollapsed}
            />
          ))}
        </div>
      )}
    </>
  );
}

